<template>
  <header>
    <b-navbar class="app-header" fixed="top" toggleable="md" type="dark">
      <b-navbar-toggle target="nav_collapse"></b-navbar-toggle>

      <b-navbar-brand to="/">
        <img :src="logoUrl" class="mw-100 mh-100 align-top" />
      </b-navbar-brand>

      <b-collapse is-nav id="nav_collapse">
        <b-navbar-nav>
          <b-nav-item to="/cockpit" v-if="hasDashboard()">
            <span class="nav-large">
              <font-awesome-icon :icon="['fas', 'tachometer-alt']" />
              {{ $t('Webportal.Dashboard') }}
            </span>
            <span class="nav-small" v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Dashboard')">
              <font-awesome-icon :icon="['fas', 'tachometer-alt']" />
            </span>
          </b-nav-item>
          <b-nav-item to="/comparison" v-if="hasComparision()">
            <span class="nav-large">
              <font-awesome-icon :icon="['fas', 'chart-bar']" />
              {{ $t('Webportal.DashboardBMW.FocusKPI') }}
            </span>
            <span class="nav-small" v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.DashboardBMW.FocusKPI')">
              <font-awesome-icon :icon="['fas', 'chart-bar']" />
            </span>
          </b-nav-item>
           <b-nav-item to="/survey/checkexplorer" v-if="hasCheckExplorer() && localUserProfile.Permissions.includes('CheckExplorer')">
            <span class="nav-large">
              <font-awesome-icon :icon="['fas', 'file-check']" />
              {{ $t('Webportal.CheckExplorer') }}
            </span>
            <span class="nav-small" v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.CheckExplorer')">
              <font-awesome-icon :icon="['fas', 'file-check']" />
            </span>
          </b-nav-item>
          <b-nav-item to="/survey/mediaexplorer" v-if="localUserProfile.Permissions.includes('MediaExplorer')">
            <span class="nav-large">
              <font-awesome-icon :icon="['fas', 'cloud-download-alt']" />
              {{ $t('Webportal.MediaExplorer') }}
            </span>
            <span class="nav-small" v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.MediaExplorer')">
              <font-awesome-icon :icon="['fas', 'cloud-download-alt']" />
            </span>
          </b-nav-item>
          <b-nav-item to="/survey/rawdata" v-if="localUserProfile.Permissions.includes('RawData')">
            <span class="nav-large">
              <font-awesome-icon :icon="['fas', 'file-excel']" />
              {{ $t('Webportal.RawData') }}
            </span>
            <span class="nav-small" v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.RawData')">
              <font-awesome-icon :icon="['fas', 'file-excel']" />
            </span>
          </b-nav-item>
          <b-nav-item to="/survey/frequencies" v-if="localUserProfile.Permissions.includes('Frequencies')">
            <span class="nav-large">
              <font-awesome-icon :icon="['fas', 'dice']" />
              {{ $t('Webportal.Frequencies') }}
            </span>
            <span class="nav-small" v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Frequencies')">
              <font-awesome-icon :icon="['fas', 'dice']" />
            </span>
          </b-nav-item>
          <b-nav-item to="/survey/aggregatedreports" v-if="localUserProfile.Permissions.includes('AggregatedReports')">
            <span class="nav-large">
              <font-awesome-icon :icon="['fas', 'globe-africa']" />
              {{ $t('Webportal.AggregatedReports') }}
            </span>
            <span class="nav-small" v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.AggregatedReports')">
              <font-awesome-icon :icon="['fas', 'globe-africa']" />
            </span>
          </b-nav-item>
          <externaltools />
        </b-navbar-nav>

        <!-- Right aligned nav items -->
        <b-navbar-nav class="ml-auto">
          <b-nav-item v-if="isMSMUser" to="/administration" v-b-tooltip.hover.nofade="toolTipDelay">
            <span class="nav-large">
              <font-awesome-icon :icon="['fas', 'sliders-h']" />
              {{ $t('Webportal.Administration') }}
            </span>
            <span class="nav-small" v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration')">
              <font-awesome-icon :icon="['fas', 'sliders-h']" />
            </span>
          </b-nav-item>
          <!-- Dropdown for active language -->
          <b-nav-item-dropdown right toggle-class="navbar-highlight">
            <template slot="button-content">
              <span :title="getLanguageName(currentLocalization, LanguageName.Normal)"
                    v-b-tooltip.hover.nofade="toolTipDelay">
                <font-awesome-icon v-if="isDefaultLocalization(currentLocalization)" icon="wrench" />
                <span v-else :class="['flag-icon', `flag-icon-${getRegion(currentLocalization)}`]"></span>
                <span class="nav-large">{{ getLanguageName(currentLocalization, LanguageName.Short) }}</span>
              </span>
            </template>
            <b-dropdown-item v-for="localization in localizations"
                             :key="localization[0].localizationTextId"
                             href="#"
                             v-on:click="changeLocalization(localization)">
              <font-awesome-icon v-if="isDefaultLocalization(localization)" icon="wrench" size="lg" />
              <span v-else :class="['flag-icon', `flag-icon-${getRegion(localization)}`]"></span>
              {{ getLanguageName(localization, LanguageName.Full) }}
            </b-dropdown-item>
          </b-nav-item-dropdown>

          <b-nav-item-dropdown right toggle-class="navbar-highlight">
            <template slot="button-content">
              <span :title="userDisplayName"
                   v-b-tooltip.hover.nofade="toolTipDelay">
                <font-awesome-icon icon="user-circle" size="lg" />
                <span class="nav-large">{{ userInitials }}</span>
              </span>
            </template>
            <b-dropdown-item disabled>
              <b-button disabled variant="dark">
                <font-awesome-icon :icon="['far', 'user']" />
                <span class="pl-1">{{ userDisplayName }}</span>
              </b-button>
            </b-dropdown-item>
            <b-dropdown-divider></b-dropdown-divider>
            <b-dropdown-item to="/user/profile">
              <font-awesome-icon :icon="['far', 'user-cog']" fixed-width />
              {{ $t('Webportal.Profile') }}
            </b-dropdown-item>
            <template v-if="localUserProfile.Permissions.includes('NotificationSettings')">
              <b-dropdown-divider></b-dropdown-divider>
              <b-dropdown-item to="/user/notificationsettings">
                <font-awesome-icon :icon="['far', 'mail-bulk']" fixed-width />
                {{ $t('Webportal.NotificationSettings') }}
              </b-dropdown-item>
            </template>
            <b-dropdown-divider></b-dropdown-divider>
            <b-dropdown-item href="#" @click="logout">
              <font-awesome-icon :icon="['far', 'sign-out-alt']" fixed-width />
              {{ $t('Webportal.SignOut') }}
            </b-dropdown-item>
          </b-nav-item-dropdown>

          <!-- Question mark: download localized manual -->
          <manualdownload />
        </b-navbar-nav>
      </b-collapse>
    </b-navbar>
  </header>
</template>


<script>

import { i18n, changeLocalization } from '@/i18n'

import LocalizationsService from '@/services/LocalizationsService';
import DashboardService from '@/services/DashboardService-APP_TARGET';
import AuthenticationService from '@/services/AuthenticationService';
import UsersService from '@/services/UsersService';
import EventBus from '@/services/EventBus.js';
import AppConfig from '@/mixins/AppConfig-APP_TARGET';
import externaltools from './ExternalTools-APP_TARGET';
import manualdownload from './ManualDownload-APP_TARGET';


export const LanguageName = {
  Full: 1,
  Normal: 2,
  Short: 3,
}

export default {
  name: "AppHeader",

  mixins: [ AppConfig ],

  components:{
    externaltools, manualdownload
  },

  data() {
    return {
      localizations: {},
      manualLink: '#',
      currentLocalization: null,
      LanguageName: LanguageName,
      logoUrl: this.getCustomLogoUrl() || AuthenticationService.logoExternalImageUrl
    }
  },

  mounted() {
    LocalizationsService.getLocalizationTexts()
      .then((response) => {
        this.localizations = _(response.data)
          .filter(element => element.localizationTextId !== 'deflt' || AuthenticationService.isMSMUser)
          .groupBy('localizationTextId')
          .forEach(element => {
            element.sort((a, b) => { if (a.localizationId === 'en-GB') return 1; else
                                     if (b.localizationId === 'en-GB') return -1; else
                                     return 0; })
          });

        this.currentLocalization = this.localizations[i18n.locale]
      });

    EventBus.$on('LocalizationChanged', localizationId => {
      this.currentLocalization = this.localizations[localizationId];
    });
  },

  methods: {
    hasClass(element) {
      return element.classList;
    },

    getLanguageName(localization, languageName = LanguageName.Full) {
      if (!localization)
        return '';

      if (languageName === LanguageName.Full)
        if (localization.length > 1)
          return `${localization[0].languageName} - ${localization[1].languageName}`;
        else
          return localization[0].languageName;
      else if (languageName === LanguageName.Normal)
        return localization[0].languageName;
      else
        return localization[0].languageNameShort;
    },

    getRegion(localization) {
      if (!localization || !localization[0].localizationTextId.includes('-'))
        return '';
      else
        return localization[0].localizationTextId.split('-')[1].toLowerCase();
    },

    isDefaultLocalization(localization) {
      return localization && localization[0].localizationTextId === 'deflt';
    },

    changeLocalization(localization) {
      changeLocalization(localization[0].localizationTextId);
      UsersService.setUserProfile({ LocalizationId: localization[0].localizationTextId });
    },

    logout() {
      AuthenticationService.logout();
    }
  },

  computed: {
    isMSMUser: function () {
      return AuthenticationService.isMSMUser;
    },

    userProfile: function() {
      return AuthenticationService.userProfile;
    },

    localUserProfile: function() {
      return AuthenticationService.localUserProfile;
    },

    userDisplayName: function() {
      return `${this.userProfile.given_name} ${this.userProfile.family_name}`;
    },

    userInitials: function() {
      if (this.userProfile.given_name && this.userProfile.family_name)
        return this.userProfile.given_name.charAt(0).toUpperCase() + this.userProfile.family_name.charAt(0).toUpperCase();
      else
        return '';
    }
  },
};
</script>
